import { useMemo, useCallback } from 'react';
import { useSelectOptions } from './useSelectOptions';
import { OptionKey } from './optionTypes';
import { resolveUserEmail, resolveUserName, resolveUserNames, UserScope } from './userUtils';

/**
 * Hook that provides user resolution functions with pre-loaded options
 * @param scope - Which user types to load and search in (default: 'all')
 * @returns Object containing resolveUser and resolveUsers functions
 */
export const useUserResolution = (scope: UserScope = 'all') => {
  // Determine which option keys to load based on scope
  const optionKeys = useMemo((): OptionKey[] => {
    switch (scope) {
      case 'researcher':
        return ['staff'];
      case 'faculty':
        return ['staff'];
      case 'external':
        return ['users'];
      case 'in-house':
        return ['staff'];
      case 'all':
      default:
        return ['users', 'staff'];
    }
  }, [scope]);

  const { options } = useSelectOptions(optionKeys);

  const resolveUser = useCallback(
    (userId: string, userScope?: UserScope) => {
      // If a different scope is requested, fall back to the original function
      if (userScope && userScope !== scope) {
        return resolveUserName(userId, options, userScope);
      }
      // Use the pre-loaded options for the configured scope
      return resolveUserName(userId, options, scope);
    },
    [options, scope]
  );

  const resolveUsers = useCallback(
    (userIds: string[] | undefined, userScope?: UserScope, separator: string = ', ') => {
      // If a different scope is requested, fall back to the original function
      if (userScope && userScope !== scope) {
        return resolveUserNames(userIds, options, userScope, separator);
      }
      // Use the pre-loaded options for the configured scope
      return resolveUserNames(userIds, options, scope, separator);
    },
    [options, scope]
  );

  const resolveEmail = useCallback(
    (userId: string, userScope?: UserScope) => resolveUserEmail(userId, options, userScope ?? scope),
    [options, scope]
  );

  return {
    resolveUser,
    resolveUsers,
    resolveEmail,
    options, // Still available if needed
  };
};

/**
 * Hook for resolving only in-house users (researchers and faculty)
 * More efficient than useUserResolution('all') when external users aren't needed
 */
export const useInHouseUserResolution = () => {
  return useUserResolution('in-house');
};

/**
 * Hook for resolving only researchers
 * Most efficient when only researcher data is needed
 */
export const useResearcherResolution = () => {
  return useUserResolution('researcher');
};

/**
 * Hook for resolving only faculty
 * Most efficient when only faculty data is needed
 */
export const useFacultyResolution = () => {
  return useUserResolution('faculty');
};

/**
 * Hook for resolving only external users
 * Most efficient when only external user data is needed
 */
export const useExternalUserResolution = () => {
  return useUserResolution('external');
};
